export interface RoundAutoSync {
  /** Flush after the delay; repeated calls within the delay coalesce. */
  schedule(): void;
  /** Drop the pending flush and wait for an in-flight flush to finish. */
  settle(): Promise<void>;
}

/**
 * Flushes the round journal shortly after scoring commands. Invariants:
 * one flush at a time, a schedule during a flight runs once after it,
 * and the flush callback owns its own error reporting.
 */
export function createRoundAutoSync(
  flush: () => Promise<void>,
  delayMs: number,
): RoundAutoSync {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inFlight: Promise<void> | null = null;
  let rerun = false;

  const start = (): void => {
    timer = null;
    if (inFlight) {
      rerun = true;
      return;
    }
    inFlight = flush().finally(() => {
      inFlight = null;
      if (!rerun) return;
      rerun = false;
      start();
    });
  };

  return {
    schedule() {
      if (timer !== null) clearTimeout(timer);
      timer = setTimeout(start, delayMs);
    },
    async settle() {
      if (timer !== null) clearTimeout(timer);
      timer = null;
      rerun = false;
      await inFlight;
    },
  };
}
